import { Checkbox } from '@components/Checkbox';
import { FormControlLabel, Stack, Typography } from '@mui/material';
import { BirthSex } from '@src/api';
import { FC, useContext } from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';
import { RuleFactoryChildrenProps } from '../types';
import { DisableContext } from '@pages/Selection/components/DisableContext';
import { DisabledRuleItem } from '@components/DisabledRuleItem';
import { buildDataTestIdAttribute, buildDataTestIdFromNestedName } from '@src/utils';

type CheckboxBirthSexProps = {
  label: string;
  setValue: (name: string, value: BirthSex[], flag: any) => void;
  name: string;
  control: any;
  disabled?: boolean;
};

const CheckboxBirthSex: FC<CheckboxBirthSexProps> = ({ label, setValue, name, control, disabled }) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, currentValue: BirthSex[]) => {
    const { checked: checkedEvent } = event.target;
    if (checkedEvent) {
      setValue(name, [...currentValue, label as BirthSex], { shouldValidate: true });
    } else {
      setValue(
        name,
        currentValue.filter(sex => sex !== label),
        { shouldValidate: true },
      );
    }
  };

  const dataTestId = buildDataTestIdFromNestedName(name, 'checkbox', label);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value }, formState }) => {
        const errorField = get(formState.errors, name);
        const error = errorField ? (errorField.message as string) : '';

        return (
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { lineHeight: '20px' } }}
            control={
              <Checkbox
                disabled={disabled}
                error={!!error}
                checked={(value || []).includes(label as BirthSex)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, value as BirthSex[])}
                inputProps={{ ...buildDataTestIdAttribute(dataTestId) } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={label}
          />
        );
      }}
    />
  );
};

const BIRTH_SEXES = [BirthSex.Male, BirthSex.Female, BirthSex.Unknown];

export const PatientBirthSexRule: FC<RuleFactoryChildrenProps> = ({ prefix, children }) => {
  const { setValue, control, formState, watch } = useFormContext();
  const birthSexes = `${prefix}.birthSexes`;
  const disabled = useContext(DisableContext);

  const birthSexesValues = watch(birthSexes)?.join(', ');

  const errorField = get(formState.errors, birthSexes);
  const error = errorField ? (errorField.message as string) : '';

  return disabled ? (
    <DisabledRuleItem name="Patient's birth sex" value={birthSexesValues} />
  ) : (
    <Stack direction="row" justifyContent="space-between" flexGrow={1}>
      <Stack gap="1px" justifyContent="center">
        <Stack gap="24px" direction="row">
          {BIRTH_SEXES.map(sex => (
            <CheckboxBirthSex
              key={sex}
              control={control}
              label={sex}
              name={birthSexes}
              setValue={setValue}
              disabled={disabled}
            />
          ))}
        </Stack>
        {error && (
          <Typography color="error.200" variant="label12Medium" lineHeight="16px" sx={{ height: 0 }}>
            Select at least one
          </Typography>
        )}
      </Stack>

      {children}
    </Stack>
  );
};
